// src/components/RepairsModal.js
import React from 'react';
import { useCart } from '../context/CartContext';
import {
  FaPlus, FaWrench, FaFan, FaPlug, FaPowerOff, FaCompactDisc,
  FaTv, FaUsb, FaGamepad, FaTools, FaCameraRetro, FaCamera,
  FaFlask, FaDatabase,
} from 'react-icons/fa';

// Icon map remains the same
const iconMap = {
  "Digitizer": FaTools, "Battery Replacement": FaPlug, "Battery Replacment": FaPlug,
  "Inner screen": FaTv, "Inner Screen Replacment": FaTv, "Screen Replacment": FaTv,
  "Charging port repair": FaPlug, "Charging Port repair": FaPlug, "Charging Port Repair": FaPlug,
  "Audio Repair": FaUsb, "Camera Repair": FaCameraRetro, "Cameras": FaCamera,
  "Camera Lens": FaCompactDisc, "Diagnostics": FaTools, "Device not turning on": FaPowerOff,
  "Repair Quotation": FaPlus, "Liquid Diagnostics": FaFlask, "Data Recovery": FaDatabase,
  "Overheating/Fan Noise": FaFan, "Overheating": FaFan, "HDMI Port Repair": FaPlug,
  "HDMI Port Replacement": FaPlug, "Console Not Turning On": FaPowerOff,
  "Unable to Insert/ Eject Disc": FaCompactDisc, "No Video Output": FaTv, "USB Repair": FaUsb,
  "Unable to update": FaTools, "Stuck Safe Mode": FaTools, "Blue Light of Death": FaPowerOff,
  "Disc Not Recognised": FaCompactDisc, "Unable to Connect to Internet": FaPowerOff,
  "Loading Screen Stuck": FaTools, "Controller Not Syncing": FaGamepad,
  "System Error (e.g E101)": FaPowerOff, "Screen Replacement": FaTv,
  "Digitizer Repair": FaTools, // Keep distinct Digitizer Repair entry if needed
  //Charging Port Repair is already defined above
  "Card Slot Repair": FaGamepad,
  default: FaWrench
};


function RepairsModal({ show, onClose, watchModel }) {
  const { addToCart } = useCart();
  const [selectedScreenTypes, setSelectedScreenTypes] = React.useState({});

  // Initialize selected screen types when modal opens
  React.useEffect(() => {
    if (show && watchModel?.repairs) {
      const initialScreenTypes = {};
      watchModel.repairs.forEach((repair, index) => {
        if (repair.screenTypes && repair.screenTypes.length > 0) {
          initialScreenTypes[index] = repair.screenTypes[0].name;
        }
      });
      setSelectedScreenTypes(initialScreenTypes);
    }
  }, [show, watchModel]);

  if (!show) return null;

  /**
   * Fly-to-cart animation using linear interpolation (straight line).
   * Targets the currently visible cart icon (desktop or mobile).
   */
  const flyToCart = (startElem) => {
    // Attempt to find both possible cart icons
    const cartIconDesktop = document.getElementById('cart-icon-desktop');
    const cartIconMobile = document.getElementById('cart-icon-mobile');

    let targetCartIcon = null;

    // Determine which cart icon is currently visible
    if (cartIconDesktop && cartIconDesktop.offsetParent !== null && cartIconDesktop.getBoundingClientRect().width > 0) {
        targetCartIcon = cartIconDesktop;
    } else if (cartIconMobile && cartIconMobile.offsetParent !== null && cartIconMobile.getBoundingClientRect().width > 0) {
        targetCartIcon = cartIconMobile;
    }

    // If no visible cart icon is found, or start element is missing, exit.
    if (!targetCartIcon || !startElem) {
        console.warn("Fly-to-cart: Could not find a visible target cart icon or start element.");
        return;
    }


    const startRect = startElem.getBoundingClientRect();
    const endRect = targetCartIcon.getBoundingClientRect();

    // Start coordinates (center of the clicked button)
    const startX = startRect.left + startRect.width / 2;
    const startY = startRect.top + startRect.height / 2;

    // End coordinates (center of the *visible* cart icon)
    const endX = endRect.left + endRect.width / 2;
    const endY = endRect.top + endRect.height / 2;

    const duration = 700; // Animation duration in milliseconds
    let startTime;

    // Create the floating element with a "+1" badge.
    const flyElem = document.createElement('div');
    flyElem.innerHTML = `
      <div style="
        position: fixed;
        background: #007BFF; /* Kentech Blue */
        color: #fff;
        width: 28px;
        height: 28px;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        font-weight: bold;
        font-size: 14px;
        pointer-events: none;
        z-index: 999999;
        box-shadow: 0 4px 8px rgba(0,0,0,0.2); /* Add shadow */
      ">+1</div>
    `;
    const flyingItem = flyElem.firstElementChild;

    // Set initial position (center the element at start)
    flyingItem.style.left = `${startX - 14}px`; // Adjust for half width
    flyingItem.style.top = `${startY - 14}px`; // Adjust for half height
    document.body.appendChild(flyingItem);

    const animate = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const elapsed = timestamp - startTime;
      const t = Math.min(elapsed / duration, 1); // Normalize time: 0 -> 1

      // Linear interpolation for straight line path
      const currentX = (1 - t) * startX + t * endX;
      const currentY = (1 - t) * startY + t * endY;

      // Apply transformations: scale down and fade out
      const scale = 1 - 0.7 * t; // Element shrinks as it travels
      flyingItem.style.left = `${currentX - 14 * scale}px`; // Adjust position based on scale
      flyingItem.style.top = `${currentY - 14 * scale}px`; // Adjust position based on scale
      flyingItem.style.transform = `scale(${scale})`; // Only apply scale
      flyingItem.style.opacity = `${1 - t}`; // Fade out

      if (t < 1) {
        requestAnimationFrame(animate);
      } else {
        // Animation complete: remove the element and trigger cart pulse
        if (flyingItem.parentNode) {
          flyingItem.parentNode.removeChild(flyingItem);
        }
        window.dispatchEvent(new Event('cartPulse')); // Trigger pulse effect on Navbar
      }
    };

    requestAnimationFrame(animate);
  };

  const handleAddRepair = (repair, repairIndex, e) => {
    // Determine the price
    let initialCost = parseFloat(String(repair.price).replace(/[^0-9.]/g, '')) || 0;
    let selectedScreenType = null;

    // Check if this repair has a screenTypeName (it's from a grouped screen replacement)
    if (repair.screenTypeName) {
      selectedScreenType = repair.screenTypeName;
    }

    const cartItem = {
      // Ensure a unique ID for the cart item itself
      id: `${watchModel.name}-${repair.repair}-${Date.now()}`,
      name: `${watchModel.name} - ${repair.repair}`,
      cost: initialCost,
      image: watchModel.image,
      // Include selected screen type if available
      ...(selectedScreenType && {
        selectedScreenType: selectedScreenType
      })
    };
    addToCart(cartItem);
    flyToCart(e.currentTarget); // Pass the clicked button element
  };

  // Close modal if backdrop is clicked
  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  return (
    <div
      style={{ /* Backdrop styles */
        position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh',
        backgroundColor: 'rgba(0,0,0,0.6)', zIndex: 9999, display: 'flex',
        alignItems: 'center', justifyContent: 'center',
      }}
      onClick={handleBackdropClick}
    >
      <div
        style={{ /* Modal content styles */
          background: '#fff', padding: '2rem', borderRadius: '8px',
          width: '450px', maxWidth: '90%', maxHeight: '85vh',
          overflowY: 'auto', position: 'relative', color: '#6c757d', // Set base text color
          boxShadow: '0 5px 15px rgba(0,0,0,0.3)',
        }}
        onClick={(e) => e.stopPropagation()} // Prevent closing when clicking inside
      >
        <h2 style={{ marginBottom: '1.5rem', textAlign: 'center', color: '#6c757d' }}>
          Repairs for {watchModel?.name || 'Selected Device'}
        </h2>

        {/* Close button top right */}
        <button
          onClick={onClose}
          style={{
            position: 'absolute', top: '10px', right: '10px', background: 'none',
            border: 'none', fontSize: '1.5rem', cursor: 'pointer', color: '#6c757d',
            lineHeight: '1'
          }}
          aria-label="Close repairs modal"
        >&times;</button>


        {!watchModel?.repairs || watchModel.repairs.length === 0 ? (
          <p style={{ textAlign: 'center' }}>No specific repairs listed for this device. Please contact us for details.</p>
        ) : (
          <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
            {(() => {
              // Group repairs: all "Screen Replacement - X" repairs go together
              const screenReplacements = [];
              const otherRepairs = [];

              watchModel.repairs.forEach((repair, index) => {
                if (repair.repair && repair.repair.startsWith('Screen Replacement - ')) {
                  const screenTypeName = repair.repair.substring('Screen Replacement - '.length);
                  screenReplacements.push({
                    ...repair,
                    screenTypeName,
                    originalIndex: index
                  });
                } else {
                  otherRepairs.push({ ...repair, originalIndex: index });
                }
              });

              const allItems = [];

              // Add grouped screen replacements first (if any)
              if (screenReplacements.length > 0) {
                allItems.push({
                  isGroup: true,
                  options: screenReplacements
                });
              }

              // Add other repairs
              otherRepairs.forEach(repair => {
                allItems.push({
                  isGroup: false,
                  repair
                });
              });

              return allItems.map((item, itemIndex) => {
                if (item.isGroup) {
                  // Screen replacement group with dropdown
                  const selectedOption = selectedScreenTypes[`group-${itemIndex}`] || item.options[0].screenTypeName;
                  const selectedRepair = item.options.find(opt => opt.screenTypeName === selectedOption) || item.options[0];
                  const RepairIcon = iconMap['Screen Replacement'] || iconMap.default;
                  const displayPrice = `£${String(selectedRepair.price).replace(/[^0-9.]/g, '')}`;

                  return (
                    <li
                      key={`group-${itemIndex}`}
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        marginBottom: '1rem',
                        borderBottom: '1px solid #eee',
                        padding: '0.75rem 0.5rem',
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.75rem' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.8rem', flex: 1 }}>
                          <RepairIcon size={22} style={{ color: '#007BFF', flexShrink: 0 }}/>
                          <span style={{ fontWeight: '500' }}>Screen Replacement</span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.8rem' }}>
                          <strong style={{ color: '#6c757d', minWidth: '50px', textAlign: 'right' }}>{displayPrice}</strong>
                          <button
                            style={{
                              display: 'inline-flex', alignItems: 'center', background: '#007BFF',
                              color: '#fff', border: 'none', borderRadius: '4px',
                              padding: '0.4rem 0.6rem', cursor: 'pointer', transition: 'background-color 0.2s',
                            }}
                            onClick={(e) => handleAddRepair(selectedRepair, selectedRepair.originalIndex, e)}
                            onMouseOver={(e) => e.currentTarget.style.backgroundColor='#0056b3'}
                            onMouseOut={(e) => e.currentTarget.style.backgroundColor='#007BFF'}
                            aria-label="Add Screen Replacement repair to cart"
                          >
                            <FaPlus size={14}/>
                          </button>
                        </div>
                      </div>

                      {/* Screen Type Dropdown */}
                      <div style={{ marginTop: '0.5rem', paddingLeft: window.innerWidth <= 480 ? '0' : '30px' }}>
                        <label
                          htmlFor={`screen-type-group-${itemIndex}`}
                          style={{
                            fontSize: window.innerWidth <= 480 ? '14px' : '13px',
                            color: '#495057',
                            marginBottom: '0.5rem',
                            display: 'block',
                            fontWeight: '500'
                          }}
                        >
                          Screen Type:
                        </label>
                        <select
                          id={`screen-type-group-${itemIndex}`}
                          value={selectedOption}
                          onChange={(e) => {
                            setSelectedScreenTypes(prev => ({
                              ...prev,
                              [`group-${itemIndex}`]: e.target.value
                            }));
                          }}
                          style={{
                            padding: window.innerWidth <= 480 ? '12px 14px' : '10px 12px',
                            border: '2px solid #007BFF',
                            borderRadius: '6px',
                            fontSize: window.innerWidth <= 480 ? '15px' : '14px',
                            backgroundColor: '#f8f9fa',
                            color: '#212529',
                            cursor: 'pointer',
                            width: '100%',
                            minHeight: '44px',
                            outline: 'none',
                            transition: 'all 0.2s ease',
                            boxShadow: '0 2px 4px rgba(0,123,255,0.1)',
                            appearance: 'none',
                            backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%23007BFF' d='M6 9L1 4h10z'/%3E%3C/svg%3E")`,
                            backgroundRepeat: 'no-repeat',
                            backgroundPosition: 'right 12px center',
                            paddingRight: '36px',
                            fontWeight: '500'
                          }}
                          onFocus={(e) => {
                            e.currentTarget.style.borderColor = '#0056b3';
                            e.currentTarget.style.boxShadow = '0 0 0 3px rgba(0,123,255,0.15)';
                          }}
                          onBlur={(e) => {
                            e.currentTarget.style.borderColor = '#007BFF';
                            e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,123,255,0.1)';
                          }}
                        >
                          {item.options.map((option, optIdx) => (
                            <option key={optIdx} value={option.screenTypeName}>
                              {option.screenTypeName} - £{String(option.price).replace(/[^0-9.]/g, '')}
                            </option>
                          ))}
                        </select>
                      </div>
                    </li>
                  );
                } else {
                  // Regular repair
                  const repair = item.repair;
                  const RepairIcon = iconMap[repair.repair] || iconMap.default;
                  const displayPrice = repair.price ? `£${String(repair.price).replace(/[^0-9.]/g, '')}` : 'Inquire';

                  return (
                    <li
                      key={`repair-${itemIndex}`}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        marginBottom: '1rem',
                        borderBottom: '1px solid #eee',
                        padding: '0.75rem 0.5rem',
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.8rem' }}>
                        <RepairIcon size={22} style={{ color: '#007BFF', flexShrink: 0 }}/>
                        <span style={{ fontWeight: '500' }}>{repair.repair}</span>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.8rem' }}>
                        <strong style={{ color: '#6c757d', minWidth: '50px', textAlign: 'right' }}>{displayPrice}</strong>
                        <button
                          style={{
                            display: 'inline-flex', alignItems: 'center', background: '#007BFF',
                            color: '#fff', border: 'none', borderRadius: '4px',
                            padding: '0.4rem 0.6px', cursor: 'pointer', transition: 'background-color 0.2s',
                          }}
                          onClick={(e) => handleAddRepair(repair, repair.originalIndex, e)}
                          onMouseOver={(e) => e.currentTarget.style.backgroundColor='#0056b3'}
                          onMouseOut={(e) => e.currentTarget.style.backgroundColor='#007BFF'}
                          aria-label={`Add ${repair.repair} repair to cart`}
                        >
                          <FaPlus size={14}/>
                        </button>
                      </div>
                    </li>
                  );
                }
              });
            })()}
          </ul>
        )}
        {/* Keep the main close button at the bottom if preferred */}
        {/* <button
            style={{ ...styles for bottom close button ... }}
            onClick={onClose}
         >
           Close
         </button> */}
      </div>
    </div>
  );
}

export default RepairsModal;